/** Prefijos por pantalla (misma estructura de datos: id → grados 0/90/180/270) */
const STORAGE_PREFIX = {
  /** Texto → imagen (`/social/ai/text-to-image`) */
  textToImage: "wembii-text-to-image-rotations:",
  /** Creador / editor con referencias (`/social/ai-generation`) — imágenes generadas */
  aiGeneration: "wembii-ai-generation-rotations:",
} as const;

export type ImageRotationScope = keyof typeof STORAGE_PREFIX;

function normalizeDeg(deg: number): number {
  const n = ((Math.round(deg) % 360) + 360) % 360;
  if (n === 0 || n === 90 || n === 180 || n === 270) return n;
  return 0;
}

export function getImageRotationStorageKey(
  scope: ImageRotationScope,
  companyId: string
): string {
  return `${STORAGE_PREFIX[scope]}${companyId}`;
}

/** Lee rotaciones guardadas (solo ángulos válidos 0/90/180/270). */
export function loadImageRotations(
  scope: ImageRotationScope,
  companyId: string
): Record<string, number> {
  try {
    const raw = localStorage.getItem(getImageRotationStorageKey(scope, companyId));
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const out: Record<string, number> = {};
    for (const [k, v] of Object.entries(parsed)) {
      if (typeof v === "number" && !Number.isNaN(v)) {
        const n = normalizeDeg(v);
        if (n !== 0) out[k] = n;
      }
    }
    return out;
  } catch {
    return {};
  }
}

/** Guarda solo entradas con rotación distinta de 0; si queda vacío, elimina la clave. */
export function saveImageRotations(
  scope: ImageRotationScope,
  companyId: string,
  rotations: Record<string, number>
): void {
  try {
    const filtered: Record<string, number> = {};
    for (const [id, deg] of Object.entries(rotations)) {
      const n = normalizeDeg(deg);
      if (n !== 0) filtered[id] = n;
    }
    const key = getImageRotationStorageKey(scope, companyId);
    if (Object.keys(filtered).length === 0) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, JSON.stringify(filtered));
    }
  } catch {
    // quota / private mode
  }
}

/** @deprecated usar `getImageRotationStorageKey("textToImage", companyId)` */
export function getTextToImageRotationStorageKey(companyId: string): string {
  return getImageRotationStorageKey("textToImage", companyId);
}

export function loadTextToImageRotations(companyId: string): Record<string, number> {
  return loadImageRotations("textToImage", companyId);
}

export function saveTextToImageRotations(
  companyId: string,
  rotations: Record<string, number>
): void {
  saveImageRotations("textToImage", companyId, rotations);
}

export function loadAiGenerationRotations(companyId: string): Record<string, number> {
  return loadImageRotations("aiGeneration", companyId);
}

export function saveAiGenerationRotations(
  companyId: string,
  rotations: Record<string, number>
): void {
  saveImageRotations("aiGeneration", companyId, rotations);
}
